import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { initializeTokenMonitoring, handleTokenExpiration } from '@/lib/tokenManager';

/**
 * Component that monitors token expiration and automatically handles refresh/logout
 * Should be included in the app layout or root component
 */
export default function TokenExpirationHandler() {
  const { isAuthenticated, isLoading, accessToken, backendToken } = useAuth();
  const cleanupRef = useRef<(() => void) | null>(null);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Don't do anything while loading - wait for it to complete
    if (isLoading) {
      return;
    }

    // Only check once per auth state to avoid infinite redirect loops
    if (hasCheckedRef.current && isAuthenticated) {
      return;
    }

    hasCheckedRef.current = true;

    // Skip monitoring when unauthenticated -- do NOT clear tokens here.
    // Clearing tokens proactively causes race conditions during auth
    // callbacks and MSAL initialization. Token cleanup is handled by
    // explicit logout (localLogout) and token expiry in getBackendToken().
    if (!isAuthenticated) {
      return;
    }

    // Build a session-like object for the tokenManager
    const sessionInfo = {
      accessToken: accessToken || undefined,
    };

    // Don't check immediately - let the app load first
    // Only check if we're not on the login page
    if (window.location.pathname !== '/login') {
      // Check tokens with a delay to avoid race conditions
      setTimeout(() => {
        handleTokenExpiration(sessionInfo).catch(() => {});
      }, 2000); // 2 second delay to ensure session is fully established
    }

    // Initialize periodic monitoring (check every 10 minutes to avoid being too aggressive)
    cleanupRef.current = initializeTokenMonitoring(sessionInfo, 600000); // 10 minutes

    // Cleanup on unmount or session change
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [isAuthenticated, isLoading, accessToken, backendToken]);

  // Separate effect to reset check flag when unauthenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      hasCheckedRef.current = false;
    }
  }, [isAuthenticated, isLoading]);

  // This component doesn't render anything
  return null;
}
